import type { ExploreImageView } from '@vizit/api';
import { Asset } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { Key } from 'react';

interface ExploreAssetProps {
  /**
   * The image data to display
   * @required
   */
  image: ExploreImageView;
  /**
   * Whether to show the score bar
   * @optional
   * @default true
   */
  showScoreBar?: boolean;
  /**
   * Callback when asset is clicked
   * @optional
   */
  onAssetClick?: (imageId: string) => void;
  /**
   * Additional CSS class name
   * @optional
   */
  className?: string;
  /**
   * Additional CSS class name for the image container
   * @optional
   */
  imageClassName?: string;
}

/**
 * ExploreAsset component that displays an explore image with score badge.
 * This is a presentation component that delegates click handling to the parent.
 *
 * @example
 * <ExploreAsset
 *   image={exploreImage}
 *   showScoreBar={true}
 *   onAssetClick={(imageId) => handleImageClick(imageId)}
 * />
 */
function ExploreAsset({ image, showScoreBar = true, onAssetClick, imageClassName }: ExploreAssetProps) {
  const handleAssetClick = React.useCallback(
    (imageId: Key | undefined) => {
      if (imageId) {
        onAssetClick?.(String(imageId));
      }
    },
    [onAssetClick],
  );

  return (
    <Asset
      id={image.id}
      score={image.display_score ?? null}
      showScoreBar={showScoreBar}
      onAssetClick={handleAssetClick}
    >
      <Asset.Image
        src={image.url_360 ?? image.url ?? ''}
        fallbackSrc={image.url}
        name={`${image.brand} explore image`}
        className={cx('w-full', imageClassName)}
      />
    </Asset>
  );
}

export { ExploreAsset };
export type { ExploreAssetProps };
